/*
  TQF storefront layer - interactive components (Oct 2026 speed rebuild).
  Same features as before, re-engineered so nothing runs on the main thread
  every frame:
    - hero light field + brand stars are painted ONCE, then drifted / twinkled
      by CSS transform + opacity animations (GPU compositor, see tq-perf.css)
    - every loop (autoplay timers, marquees, twinkle, countdown) pauses while
      its section is off-screen or the tab is hidden
    - scroll effects are rAF-throttled, only active while their section is in
      view, and read layout once per frame (no read/write thrash)
    - dims use overlay opacity (--tqf-dim) instead of CSS filter
  Reveal-on-scroll is handled by the inline TQMotion engine in theme.liquid;
  this file falls back to its own observer when that engine is absent.
*/
(function () {
  'use strict';
  var doc = document;
  var html = doc.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mqMobile = window.matchMedia('(max-width: 760px)');
  var fineHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var isMobile = function () { return mqMobile.matches; };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || doc).querySelectorAll(s)); };
  var rtl = html.dir === 'rtl';
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- shared visibility tracking (one observer for every live component) ---------- */
  var visCbs = typeof WeakMap === 'function' ? new WeakMap() : null;
  var visIO = hasIO && visCbs ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var list = visCbs.get(en.target);
      if (list) list.forEach(function (cb) { cb(en.isIntersecting); });
    });
  }, { rootMargin: '120px 0px 120px 0px' }) : null;
  function watch(el, cb) {
    if (!visIO) { cb(true); return; }
    var list = visCbs.get(el);
    if (!list) { list = []; visCbs.set(el, list); visIO.observe(el); }
    list.push(cb);
  }
  var pageVisible = !doc.hidden;
  var pageCbs = [];
  doc.addEventListener('visibilitychange', function () {
    pageVisible = !doc.hidden;
    pageCbs.forEach(function (cb) { cb(pageVisible); });
  });
  function onPage(cb) { pageCbs.push(cb); }
  /* toggles .tqf-paused on a host so its CSS loops stop off-screen */
  function pauseOffscreen(host) {
    watch(host, function (vis) { host.classList.toggle('tqf-paused', !vis); });
  }

  /* resize: one rAF-batched listener for everything */
  var resizeCbs = [];
  var resizeQueued = false;
  window.addEventListener('resize', function () {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(function () {
      resizeQueued = false;
      resizeCbs.forEach(function (cb) { cb(); });
    });
  }, { passive: true });
  function onResize(cb) { resizeCbs.push(cb); }

  /* ---------- header height (cached; read by the sticky stack) ----------
     speed pass 3: --tqf-hdr-h is written only when it changes (writing it on <html>
     restyles the whole page), and listeners (the card stack) hear about real changes */
  var hdrH = 110, hdrPx = -1, hdrCbs = [];
  function measureHeader() {
    var h = doc.querySelector('.tq-market-header-section');
    var hh = h ? Math.round(h.getBoundingClientRect().height) : 0;
    hdrH = hh || 110;
    if (hh === hdrPx) return;
    hdrPx = hh;
    html.style.setProperty('--tqf-hdr-h', hh + 'px');
    hdrCbs.forEach(function (cb) { cb(); });
  }
  onResize(measureHeader);
  measureHeader();
  setTimeout(measureHeader, 800);

  /* ---------- hero image accordion ----------
     speed pass 2: the panel geometry lives in tq-perf.css (transform + clip-path, GPU only);
     switching a panel just writes --o (the open index) once - no layout, no layout shift.
     Auto-advance also holds while the pointer rests on the hero. */
  function initAcc(root) {
    if (root.dataset.tqfInit) return;
    root.dataset.tqfInit = '1';
    var panels = $$('.tqf-acc__p', root);
    var dots = $$('[data-tqf-dot]', root.closest('.tqf-hero') || root);
    var idx = 0, timer = null, inView = true, hovering = false;
    var every = parseInt(root.dataset.every || '5200', 10);
    function open(i) {
      if (panels[idx] && i === idx && panels[i].classList.contains('is-open')) return;
      idx = i;
      root.style.setProperty('--o', i);
      panels.forEach(function (p, k) { p.classList.toggle('is-open', k === i); });
      dots.forEach(function (d, k) { d.classList.toggle('on', k === i); });
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function restart() {
      stop();
      if (reduced || isMobile() || every <= 0 || !inView || !pageVisible || hovering || panels.length < 2) return;
      timer = setInterval(function () { open((idx + 1) % panels.length); }, every);
    }
    root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hovering = true; stop(); } });
    root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { hovering = false; restart(); } });
    panels.forEach(function (p, i) {
      p.addEventListener('pointerenter', function () { if (!isMobile()) { open(i); restart(); } });
      p.addEventListener('click', function (e) {
        if (!isMobile() && !p.classList.contains('is-open')) { e.preventDefault(); open(i); restart(); }
      });
    });
    var scrollQueued = false;
    root.addEventListener('scroll', function () {
      if (!isMobile() || !panels.length || scrollQueued) return;
      scrollQueued = true;
      requestAnimationFrame(function () {
        scrollQueued = false;
        var w = panels[0].offsetWidth + 12;
        var i = Math.min(panels.length - 1, Math.round(Math.abs(root.scrollLeft) / w));
        dots.forEach(function (d, k) { d.classList.toggle('on', k === i); });
      });
    }, { passive: true });
    var first = panels.findIndex(function (p) { return p.classList.contains('is-open'); });
    if (first < 0 && panels.length) open(0); else { idx = Math.max(0, first); if (idx) root.style.setProperty('--o', idx); }
    watch(root, function (vis) { inView = vis; restart(); });
    onPage(restart);
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', restart);
    restart();
  }

  /* ---------- hero light field: 4 soft blobs, painted once, drifted by the GPU ---------- */
  var AURORA = {
    dark: [
      { x: .8, y: .15, r: .42, c: '95,212,216', s: 22e-5, ph: 0, a: .16 },
      { x: .2, y: .9, r: .4, c: '19,92,99', s: 17e-5, ph: 2, a: .3 },
      { x: .55, y: .5, r: .3, c: '155,235,236', s: 28e-5, ph: 4, a: .08 },
      { x: .92, y: .85, r: .35, c: '193,145,63', s: 13e-5, ph: 1, a: .14 }
    ],
    light: [
      { x: .78, y: .18, r: .55, c: '231,226,216', s: 22e-5, ph: 0, a: .9 },
      { x: .25, y: .85, r: .5, c: '226,232,232', s: 17e-5, ph: 2, a: .9 },
      { x: .55, y: .55, r: .3, c: '95,212,216', s: 28e-5, ph: 4, a: .12 },
      { x: .9, y: .8, r: .4, c: '193,145,63', s: 13e-5, ph: 1, a: .1 }
    ]
  };
  function initAurora(cv) {
    if (cv.dataset.tqfInit) return;
    cv.dataset.tqfInit = '1';
    var host = cv.parentElement;
    if (!host) return;
    var blobs = cv.dataset.tqfAurora === 'dark' ? AURORA.dark : AURORA.light;
    var wrap = doc.createElement('div');
    wrap.className = 'tqf-aurora ' + (cv.className || '');
    wrap.setAttribute('aria-hidden', 'true');
    var layers = blobs.map(function (b) {
      var xw = doc.createElement('span');
      xw.className = 'tqf-aurora__x';
      /* original motion: x = sin(t*s + ph), y = cos(t*s*1.3 + ph); a CSS ease-in-out
         alternate loop is a sine, so half-periods + phase map straight across */
      var px = (2 * Math.PI / b.s) / 1000, py = px / 1.3;
      xw.style.setProperty('--ax', (px / 2).toFixed(2) + 's');
      xw.style.setProperty('--ay', (py / 2).toFixed(2) + 's');
      xw.style.setProperty('--axd', (-(b.ph / (2 * Math.PI)) * px).toFixed(2) + 's');
      xw.style.setProperty('--ayd', (-((b.ph + Math.PI / 2) / (2 * Math.PI)) * py).toFixed(2) + 's');
      var c = doc.createElement('canvas');
      c.className = 'tqf-aurora__y';
      xw.appendChild(c);
      wrap.appendChild(xw);
      return { b: b, c: c };
    });
    cv.style.display = 'none';
    host.insertBefore(wrap, cv);
    var lastW = 0, lastH = 0;
    function paint() {
      var r = host.getBoundingClientRect();
      /* layer is 128% of the host (inset:-14%) so the drift never shows an edge;
         blobs are pure gradients, so a third of the resolution is invisible */
      var w = Math.max(160, Math.round(r.width * 1.28 / 3));
      var h = Math.max(120, Math.round(r.height * 1.28 / 3));
      if (Math.abs(w - lastW) < 8 && Math.abs(h - lastH) < 8) return;
      lastW = w; lastH = h;
      var ow = w / 1.28, oh = h / 1.28, ox = (w - ow) / 2, oy = (h - oh) / 2;
      layers.forEach(function (L) {
        var b = L.b, c = L.c;
        c.width = w; c.height = h;
        var ctx = c.getContext('2d');
        var x = ox + b.x * ow, y = oy + b.y * oh, rr = b.r * Math.max(ow, oh);
        var g = ctx.createRadialGradient(x, y, 0, x, y, rr);
        g.addColorStop(0, 'rgba(' + b.c + ',' + b.a + ')');
        g.addColorStop(.5, 'rgba(' + b.c + ',' + (b.a * .35) + ')');
        g.addColorStop(1, 'rgba(' + b.c + ',0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      });
    }
    paint();
    requestAnimationFrame(function () { wrap.classList.add('is-on'); });
    onResize(paint);
    pauseOffscreen(host);
  }

  /* ---------- brand horizon stars: 3 pre-drawn layers that cross-fade ---------- */
  function initStars(cv) {
    if (cv.dataset.tqfInit) return;
    cv.dataset.tqfInit = '1';
    var host = cv.parentElement;
    if (!host) return;
    var wrap = doc.createElement('div');
    wrap.className = 'tqf-stars';
    wrap.setAttribute('aria-hidden', 'true');
    var LAYERS = [{ tw: 3.1, d: 0 }, { tw: 4.3, d: -1.7 }, { tw: 5.7, d: -3.9 }];
    var canvases = LAYERS.map(function (L) {
      var c = doc.createElement('canvas');
      c.style.setProperty('--tw', L.tw + 's');
      c.style.setProperty('--twd', L.d + 's');
      wrap.appendChild(c);
      return c;
    });
    cv.style.display = 'none';
    host.insertBefore(wrap, cv);
    var lastW = 0;
    function paint() {
      var r = host.getBoundingClientRect();
      var w = Math.floor(r.width), h = Math.floor(r.height);
      if (!w || !h || Math.abs(w - lastW) < 24) return;
      lastW = w;
      var ctxs = canvases.map(function (c) { c.width = w; c.height = h; return c.getContext('2d'); });
      var n = Math.floor(w / 6);
      for (var i = 0; i < n; i++) {
        var ctx = ctxs[i % ctxs.length];
        var x = Math.random() * w, y = Math.random() * h * .75, rr = Math.random() * 1.3 + .3;
        ctx.fillStyle = 'rgba(19,43,44,' + (.28 * (.55 + Math.random() * .45)).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(x, y, rr, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    paint();
    onResize(paint);
    pauseOffscreen(host);
  }

  /* ---------- brand marquee ---------- */
  function initMarquee(track) {
    if (track.dataset.tqfDup) return;
    track.dataset.tqfDup = '1';
    track.innerHTML += track.innerHTML;
    pauseOffscreen(track.closest('.tqf-marquee') || track.parentElement || track);
  }

  /* ---------- "shop by look" 3D cover flow ---------- */
  function initCover(root) {
    if (root.dataset.tqfInit) return;
    root.dataset.tqfInit = '1';
    var stage = root.querySelector('.tqf-cover');
    if (!stage) return;
    var cards = $$('.tqf-cover__card', stage);
    var n = cards.length;
    if (!n) return;
    var li = 0, dragging = false, dx = 0, lx = 0, timer = null, inView = true;
    var every = parseInt(root.dataset.every || '4200', 10);
    var dots = root.querySelector('[data-tqf-dots]');
    var tEl = root.querySelector('[data-tqf-title]');
    var dEl = root.querySelector('[data-tqf-desc]');
    var rEl = root.querySelector('[data-tqf-rooms]');
    var pEl = root.querySelector('[data-tqf-pal]');
    var cta = root.querySelector('[data-tqf-cta]');
    if (dots) cards.forEach(function (c, i) {
      var b = doc.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', c.dataset.title || 'Look ' + (i + 1));
      b.addEventListener('click', function () { set(i); });
      dots.appendChild(b);
    });
    var dotBtns = dots ? $$('button', dots) : [];
    function txt(el, v) { if (el && el.textContent !== v) el.textContent = v; }
    function layout() {
      var m = isMobile(), gap = m ? 150 : 230, depth = m ? 140 : 180, dir = rtl ? -1 : 1;
      cards.forEach(function (c, i) {
        var o = i - li;
        if (o > n / 2) o -= n;
        if (o < -n / 2) o += n;
        var ao = Math.abs(o), sign = Math.sign(o) * dir;
        c.style.transform = 'translateX(' + (o * gap * dir + (ao ? sign * 30 : 0)) + 'px) translateZ(' + (-ao * depth) + 'px) rotateY(' + (-sign * Math.min(ao, 1) * 38) + 'deg) scale(' + (1 - ao * .06) + ')';
        c.style.zIndex = 20 - ao;
        c.style.opacity = ao > 3 ? 0 : 1;
        c.style.setProperty('--tqf-dim', ao ? Math.min(.56, ao * .14).toFixed(2) : '0');
        c.classList.toggle('is-active', !o);
      });
      dotBtns.forEach(function (b, i) { b.classList.toggle('is-on', i === li); });
      var a = cards[li];
      txt(tEl, a.dataset.title || '');
      txt(dEl, a.dataset.desc || '');
      txt(rEl, a.dataset.rooms || '');
      txt(pEl, a.dataset.pal || '');
      if (cta) {
        if (a.dataset.url) { cta.href = a.dataset.url; cta.hidden = false; } else cta.hidden = true;
      }
    }
    function set(i) { li = ((i % n) + n) % n; layout(); restart(); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function restart() {
      stop();
      if (reduced || every <= 0 || !inView || !pageVisible) return;
      timer = setInterval(function () { li = (li + 1) % n; layout(); }, every);
    }
    cards.forEach(function (c, i) {
      c.addEventListener('click', function (e) {
        if (i !== li) { e.preventDefault(); set(i); } else if (Math.abs(dx) > 8) e.preventDefault();
      });
    });
    var prev = root.querySelector('.prev'), next = root.querySelector('.next');
    if (prev) prev.addEventListener('click', function () { set(li - 1); });
    if (next) next.addEventListener('click', function () { set(li + 1); });
    stage.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
        e.preventDefault();
        var d = e.deltaX || e.deltaY;
        if (Math.abs(d) > 18) set(li + (d > 0 ? 1 : -1));
      }
    }, { passive: false });
    stage.addEventListener('pointerdown', function (e) { dragging = true; lx = e.clientX; dx = 0; });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      dx += e.clientX - lx;
      lx = e.clientX;
      if (Math.abs(dx) > 60) { set(li + (dx < 0 ? 1 : -1)); dx = 0; }
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      stage.addEventListener(ev, function () { dragging = false; });
    });
    layout();
    watch(root, function (vis) { inView = vis; restart(); });
    onPage(restart);
    onResize(layout);
    restart();
  }

  /* ---------- Supply & Apply sticky card stack ----------
     speed pass 3: the recede (scale + dim as the next card slides over) is a CSS
     scroll-driven animation (tq-perf.css section 7) that the compositor runs in step with
     the scroll. This only MEASURES the stack - at start, then again when its size, the
     window or the header height changes - and writes each card's scroll range
     (--tqf-r0 / --tqf-r1, px into the stack's view timeline). Nothing runs while scrolling.
     Card i recedes from when card i+1 is 70% of a screen below card i's sticky top until
     card i+1 reaches it - the same curve as before.
     Fallback (no scroll-driven animations): one rect read per frame, arithmetic from the
     cached offsets, and only cards whose value changed are written. */
  var SDA = !!(window.CSS && CSS.supports && CSS.supports('(animation-timeline: view()) and (animation-range: cover 0px cover 1px)'));
  function initStack(root) {
    if (root.dataset.tqfInit) return;
    root.dataset.tqfInit = '1';
    var scs = $$('.tqf-sc', root);
    var n = scs.length;
    if (n < 2) return;
    var flow = [], st = [], vh = 0, cur = [], ready = false, inView = false, queued = false, mQueued = false;
    function measure() {
      mQueued = false;
      if (!root.isConnected) return;
      var gap = parseFloat(getComputedStyle(root).rowGap) || 0, f = 0;
      vh = html.clientHeight || window.innerHeight;
      for (var i = 0; i < n; i++) {
        flow[i] = f; /* in-flow offset inside the stack (offsetTop would include the sticky shift) */
        f += scs[i].offsetHeight + gap;
        var t = parseFloat(getComputedStyle(scs[i]).top);
        st[i] = isNaN(t) ? hdrH + 24 + i * 20 : t;
      }
      ready = f > 0;
      if (!ready) return;
      if (SDA) {
        for (var j = 0; j < n - 1; j++) {
          var b = flow[j + 1] - st[j];
          scs[j].style.setProperty('--tqf-r0', Math.round(b + vh * .3) + 'px');
          scs[j].style.setProperty('--tqf-r1', Math.round(b + vh) + 'px');
        }
      } else queue();
    }
    function remeasure() {
      if (mQueued) return;
      mQueued = true;
      requestAnimationFrame(measure);
    }
    function clear() {
      if (!cur.length) return;
      cur = [];
      scs.forEach(function (c) { c.style.transform = ''; c.style.removeProperty('--tqf-dim'); });
    }
    function fx() {
      queued = false;
      if (!ready || !inView || !root.isConnected) return;
      if (isMobile() || reduced) { clear(); return; }
      var top = root.getBoundingClientRect().top, span = vh * .7;
      for (var i = 0; i < n - 1; i++) {
        var p = 1 - (top + flow[i + 1] - st[i]) / span;
        p = p <= 0 ? 0 : p >= 1 ? 1 : Math.round(p * 400) / 400;
        if (p === cur[i]) continue;
        cur[i] = p;
        scs[i].style.transform = p ? 'translate3d(0,' + (-p * 10).toFixed(2) + 'px,0) scale(' + (1 - p * .06).toFixed(4) + ')' : '';
        scs[i].style.setProperty('--tqf-dim', (p * .35).toFixed(3));
      }
    }
    function queue() {
      if (SDA || !inView || queued) return;
      queued = true;
      requestAnimationFrame(fx);
    }
    if (!SDA) window.addEventListener('scroll', queue, { passive: true });
    onResize(remeasure);
    hdrCbs.push(remeasure);
    if ('ResizeObserver' in window) new ResizeObserver(remeasure).observe(root);
    watch(root, function (vis) {
      inView = vis;
      if (SDA) return;
      root.classList.toggle('tqf-live', vis && !isMobile() && !reduced);
      if (vis) queue();
    });
    remeasure();
  }

  /* ---------- deal countdown (ticks only while visible) ---------- */
  function initTimer(el) {
    if (el.dataset.tqfInit) return;
    el.dataset.tqfInit = '1';
    var until = el.dataset.until ? new Date(el.dataset.until) : null;
    var inView = true, iv = null;
    function tick() {
      var now = new Date();
      var end = until && !isNaN(until) && until > now ? until : (function () { var e = new Date(now); e.setHours(23, 59, 59, 999); return e; })();
      var s = Math.max(0, Math.floor((end - now) / 1e3));
      var d = Math.floor(s / 86400);
      s %= 86400;
      var h = String(Math.floor(s / 3600)).padStart(2, '0');
      s %= 3600;
      var m = String(Math.floor(s / 60)).padStart(2, '0');
      var sec = String(s % 60).padStart(2, '0');
      el.textContent = (d ? d + 'd ' : '') + h + ':' + m + ':' + sec;
    }
    function run() {
      var on = inView && pageVisible;
      if (on && !iv) { tick(); iv = setInterval(tick, 1e3); }
      if (!on && iv) { clearInterval(iv); iv = null; }
    }
    tick();
    watch(el, function (vis) { inView = vis; run(); });
    onPage(run);
    run();
    var box = el.closest('.tqf-timer');
    if (box) pauseOffscreen(box);
  }

  /* ---------- reveal (fallback when the inline TQMotion engine is absent) ---------- */
  var rvIO = null;
  function initReveal(scope) {
    if (window.TQMotion) { window.TQMotion.scan(scope || doc); return; }
    if (!rvIO && hasIO && !reduced) {
      rvIO = new IntersectionObserver(function (es) {
        es.forEach(function (en) {
          if (en.isIntersecting) { en.target.classList.add('in'); rvIO.unobserve(en.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    }
    $$('.tqf-reveal', scope).forEach(function (el) {
      if (el.dataset.tqfRv) return;
      el.dataset.tqfRv = '1';
      if (rvIO) rvIO.observe(el); else el.classList.add('in');
    });
  }

  /* ---------- product card spotlight (desktop hover only, rAF-throttled) ---------- */
  if (fineHover) {
    var spotCard = null, spotRect = null, spotX = 0, spotY = 0, spotQueued = false;
    doc.addEventListener('pointermove', function (e) {
      var c = e.target.closest && e.target.closest('.tqf-pgrid .pcard, .tqf-rail .pcard');
      if (!c) { spotCard = null; return; }
      if (c !== spotCard) { spotCard = c; spotRect = c.getBoundingClientRect(); }
      spotX = e.clientX; spotY = e.clientY;
      if (spotQueued) return;
      spotQueued = true;
      requestAnimationFrame(function () {
        spotQueued = false;
        if (!spotCard || !spotRect) return;
        spotCard.style.setProperty('--mx', (spotX - spotRect.left) + 'px');
        spotCard.style.setProperty('--my', (spotY - spotRect.top) + 'px');
      });
    }, { passive: true });
    window.addEventListener('scroll', function () { spotCard = null; }, { passive: true });
  }

  /* ---------- boot ---------- */
  function boot(scope) {
    scope = scope || doc;
    $$('[data-tqf-acc]', scope).forEach(initAcc);
    $$('canvas[data-tqf-aurora]', scope).forEach(initAurora);
    $$('canvas[data-tqf-stars]', scope).forEach(initStars);
    $$('[data-tqf-marquee]', scope).forEach(initMarquee);
    $$('[data-tqf-cover]', scope).forEach(initCover);
    $$('[data-tqf-stack]', scope).forEach(initStack);
    $$('[data-tqf-timer]', scope).forEach(initTimer);
    /* pure-CSS loops (review columns, tickers) also stop while off-screen */
    $$('.tqf-cols, .tqp-ticker', scope).forEach(function (el) {
      if (el.dataset.tqfPz) return;
      el.dataset.tqfPz = '1';
      pauseOffscreen(el);
    });
    initReveal(scope);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  doc.addEventListener('shopify:section:load', function (e) { boot(e.target); measureHeader(); });
  doc.addEventListener('shopify:block:select', function (e) {
    var p = e.target.closest && e.target.closest('.tqf-acc__p');
    if (p) {
      var all = $$('.tqf-acc__p', p.parentElement);
      all.forEach(function (x) { x.classList.toggle('is-open', x === p); });
      p.parentElement.style.setProperty('--o', all.indexOf(p));
    }
  });
})();
